import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { AiOutlineDownload, AiOutlineSearch, AiOutlineDown } from "react-icons/ai";
import { Button, Dropdown } from "react-bootstrap";
import authService from "../../services/authService";

// --- REAL API SECTION ---
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/admin/documents`;

export async function apiGetDownloadDetails() {
  const response = await authService.adminApiCall(`${API_BASE_URL}/downloads/`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch user downloads');
  }
  
  const data = await response.json();
  return data.data || [];
}

export async function apiDownloadFile({ filename, id }) {
  // For downloads, we'll just show an alert since we don't have actual file URLs
  // In a real implementation, this would trigger the actual file download
  // Download started for file
}


// --- COMPONENT ---
export default function UserDownloads() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("All Years");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const info = await apiGetDownloadDetails();
        setData(info);
      } catch (error) {
        console.error('Failed to fetch downloads:', error);
        setData([]);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Group downloads by year
  const downloadsByYear = useMemo(() => {
    const byYear = {};
    data.forEach((d) => {
      byYear[d.form_year] = byYear[d.form_year] || [];
      byYear[d.form_year].push(d);
    });
    return byYear;
  }, [data]);

  const years = useMemo(
    () => Object.keys(downloadsByYear).sort((a, b) => b - a),
    [downloadsByYear]
  );

  const filteredData = useMemo(() => {
    let filtered = data;
    
    // Filter by year
    if (selectedYear !== "All Years") {
      filtered = filtered.filter(d => d.form_year === selectedYear);
    }
    
    // Filter by search
    const s = search.trim().toLowerCase();
    if (s) {
      filtered = filtered.filter(
        d =>
          (d.form_title && d.form_title.toLowerCase().includes(s)) ||
          (d.user_name && d.user_name.toLowerCase().includes(s)) ||
          (d.user_mobile && d.user_mobile.toLowerCase().includes(s)) ||
          (d.form_category && d.form_category.toLowerCase().includes(s))
      );
    }
    
    return filtered;
  }, [data, search, selectedYear]);

  const columns = useMemo(() => [
    {
      name: "S.No",
      width: "65px",
      cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
      style: { fontWeight: 600, color: "#4338ca", textAlign: "center", whiteSpace: "nowrap" },
      sortable: false,
    },
    { 
      name: "Name", 
      selector: row => row.user_name, 
      sortable: true, 
      minWidth: "120px", 
      style: { fontWeight: 500, whiteSpace: "nowrap" },
      cell: row => <span>{row.user_name || 'N/A'}</span>
    },
    { 
      name: "Mobile", 
      selector: row => row.user_mobile, 
      sortable: true, 
      minWidth: "120px", 
      style: { color: "#232", whiteSpace: "nowrap", fontSize: 15 },
      cell: row => <span>{row.user_mobile || 'N/A'}</span>
    },
    { 
      name: "Category", 
      selector: row => row.form_category, 
      sortable: true, 
      minWidth: "140px", 
      style: { color: "#2979f2", fontWeight: 500, whiteSpace: "nowrap" },
      cell: row => <span>{row.form_category || 'N/A'}</span>
    },
    { 
      name: "Year", 
      selector: row => row.form_year, 
      sortable: true, 
      width: "80px", 
      style: { fontWeight: 600, whiteSpace: "nowrap", color: "#444", textAlign: "center" },
      cell: row => <span>{row.form_year || 'N/A'}</span>
    },
    {
      name: "Filename",
      selector: row => row.form_title,
      sortable: true,
      minWidth: "200px",
      cell: row => (
        <button
          onClick={() => handleDownload(row)}
          style={{
            background: "none", border: "none", color: "#2563eb", cursor: "pointer",
            fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6, padding: 0, whiteSpace: "nowrap"
          }}
        >
          <AiOutlineDownload /> {row.form_title || 'N/A'}
        </button>
      )
    },
    {
      name: "Datetime",
      selector: row => row.downloaded_at,
      sortable: true,
      minWidth: "170px",
      style: { color: "#444", fontSize: 15, whiteSpace: "nowrap" },
      cell: row => <span>{row.downloaded_at ? new Date(row.downloaded_at).toLocaleString() : 'N/A'}</span>
    },
  ], [currentPage, perPage]);

  async function handleDownload(row) {
    setLoading(true);
    await apiDownloadFile({ id: row.id, filename: row.form_title });
    setLoading(false);
  }

  function handlePageChange(page) { setCurrentPage(page); }
  function handlePerRowsChange(newPerPage, page) { setPerPage(newPerPage); setCurrentPage(page); }
  
  // Reset filters
  function resetFilters() {
    setSearch("");
    setSelectedYear("All Years");
    setCurrentPage(1);
  }

  const customStyles = {
    rows: { style: { fontSize: 15, background: "#f8fafc", whiteSpace: "nowrap" } },
    table: { style: { width: "100%", minWidth: 1050, background: "#fff", borderRadius: 20 } },
    headRow: { style: { fontWeight: 700, fontSize: 15, background: "#eceffd", color: "#4338ca" } },
    headCells: { style: { borderBottom: "1.5px solid #a5b4fc", padding: "9px 14px" } },
    cells: { style: { padding: "9px 14px" } },
    pagination: { style: { borderTop: "solid 1px #eceffd", color: "#4338ca", fontWeight: 600 } },
  };

  return (
    <section style={{
      userSelect: "none",
      width: "100%",
      //maxWidth: 1250,
      //margin: "0 auto",
      padding: 18
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10, marginBottom: 22
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <AiOutlineDownload style={{ fontSize: 32, color: "#215ea7" }} />
          <span style={{
            //fontWeight: 800, color: "#4338ca", fontSize: 27, letterSpacing: "0.05em",
            display: "flex",
                alignItems: "center", gap: 13,
                marginBottom: 13,
                fontSize: 38,
                marginRight: 10,
                color: "#215ea7",
                verticalAlign: "middle",
                filter: "drop-shadow(0px 1px 4px #b5beff77)",
          }}>
            User Downloads
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <input
            type="text"
            placeholder="Search filename or name"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: "8px 38px 8px 13px",
              borderRadius: 7, border: "1.5px solid #a5b4fc",
              fontSize: 15, maxWidth: 260, outline: "#4338ca", background: "#f4f9fc"
            }}
          />
          <AiOutlineSearch style={{ fontSize: 23, color: "#217eed", marginLeft: -30, pointerEvents: "none" }} />
          
          <Dropdown style={{ marginLeft: 18 }}>
            <Dropdown.Toggle
              variant="outline-primary"
              style={{
                fontWeight: 600,
                fontSize: 15,
                border: "1.5px solid #a5b4fc",
                borderRadius: 7,
                padding: "8px 15px",
                background: "#f4f9fc",
                color: "#4338ca",
                display: "flex",
                alignItems: "center",
                gap: 8
              }}
            >
              {selectedYear}
              <AiOutlineDown size={14} />
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ minWidth: 150 }}>
              <Dropdown.Item
                onClick={() => setSelectedYear("All Years")}
                style={{
                  fontWeight: selectedYear === "All Years" ? 600 : 400,
                  color: selectedYear === "All Years" ? "#4338ca" : "#333"
                }}
              >
                All Years
              </Dropdown.Item>
              {years.map((year) => (
                <Dropdown.Item
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  style={{
                    fontWeight: selectedYear === year ? 600 : 400,
                    color: selectedYear === year ? "#4338ca" : "#333"
                  }}
                >
                  {year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          
          {(search || selectedYear !== "All Years") && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={resetFilters}
              style={{
                marginLeft: 10,
                fontWeight: 600,
                fontSize: 14,
                border: "1.5px solid #6c757d",
                borderRadius: 7,
                padding: "8px 15px"
              }}
            >
              Reset
            </Button>
          )}
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredData}
        highlightOnHover
        striped
        pagination
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationDefaultPage={currentPage}
        progressPending={loading}
        noHeader
        responsive
        persistTableHead
        customStyles={customStyles}
      />
    </section>
  );
}
